import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { CallDirection, CallMethod, CallOutcome, formatDuration, METHODS, OUTCOMES, SUPPORT_LEVELS } from '../calls/logCall';
import { RecordingField } from '../calls/RecordingField';
import { CRM_UPDATE_CALL } from './callLog.queries';
import {
  CallLogRow,
  checkCorrection,
  correctionFormOf,
  correctionInput,
  CorrectionField,
  CorrectionForm,
  describeCorrectionError,
  formatWhen,
  personName,
} from './callLog';

interface CorrectCallDialogProps {
  call: CallLogRow;
  /** `changed`: the call was saved, and what the screen holds about it is stale. */
  onClose: (changed: boolean) => void;
}

/**
 * Corrects a call the admin logged in this panel (crmUpdateCall): the outcome,
 * how, notes, length, support level, the recording link (card 21.7), and the
 * follow-up while it is open. Only
 * what changed is sent. The history's calls and other admins' are not offered
 * this (`editable`), and the API refuses them too.
 */
export const CorrectCallDialog = ({ call, onClose }: CorrectCallDialogProps) => {
  const [form, setForm] = useState<CorrectionForm>(() => correctionFormOf(call));
  const [refusal, setRefusal] = useState<{ message: string; field?: CorrectionField } | null>(null);
  const [update, { loading }] = useMutation(CRM_UPDATE_CALL);

  const now = useMemo(() => dayjs(), []);
  const problems = checkCorrection(form, call, now);
  const input = correctionInput(call, form);
  const changes = Object.keys(input).length;
  const followUpDone = Boolean(call.followUpDoneAt);

  const set = (change: Partial<CorrectionForm>) => {
    setForm((was) => ({ ...was, ...change }));
    setRefusal(null);
  };

  const problemOf = (field: CorrectionField) => problems[field] ?? (refusal?.field === field ? refusal.message : undefined);

  const save = async () => {
    if (!changes || Object.keys(problems).length) return;
    try {
      await update({ variables: { id: call.id, input } });
      onClose(true);
    } catch (error) {
      setRefusal(describeCorrectionError(error as Error));
    }
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={() => !loading && onClose(false)}
      aria-labelledby="correct-call-title"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void save();
        }
      }}
    >
      <DialogTitle id="correct-call-title">
        Correct this call
        <Typography variant="body2" color="text.secondary">
          {personName(call.contact, call.contactId)} · {formatWhen(call.occurredAt, now)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            select
            size="small"
            label="Outcome"
            value={form.outcome}
            onChange={(event) => set({ outcome: event.target.value as CallOutcome })}
          >
            {OUTCOMES.map((outcome) => (
              <MenuItem key={outcome.value} value={outcome.value}>
                {outcome.label}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {outcome.hint}
                </Typography>
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              size="small"
              label="How"
              value={form.method}
              onChange={(event) => set({ method: event.target.value as CallMethod })}
              sx={{ flex: 1 }}
            >
              {METHODS.map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.direction}
              onChange={(_event, value: CallDirection | null) => value && set({ direction: value })}
              aria-label="Direction"
            >
              <ToggleButton value="OUTBOUND">Outbound</ToggleButton>
              <ToggleButton value="INBOUND">Inbound</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              label="Length"
              placeholder="4:30"
              value={form.duration}
              onChange={(event) => set({ duration: event.target.value })}
              error={Boolean(problemOf('duration'))}
              helperText={problemOf('duration') ?? 'Minutes, or minutes and seconds'}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              size="small"
              label="Support level"
              value={form.supportLevel ?? ''}
              onChange={(event) => set({ supportLevel: event.target.value === '' ? null : Number(event.target.value) })}
              sx={{ flex: 1 }}
            >
              <MenuItem value="">
                <em>Not given</em>
              </MenuItem>
              {SUPPORT_LEVELS.map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  {level.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Notes"
            multiline
            minRows={3}
            maxRows={12}
            value={form.notes}
            onChange={(event) => set({ notes: event.target.value })}
            error={Boolean(problemOf('notes'))}
            helperText={problemOf('notes')}
          />

          <RecordingField
            value={form.recordingUrl}
            onChange={(recordingUrl) => set({ recordingUrl })}
            error={refusal?.field === 'recordingUrl' ? refusal.message : undefined}
            onUseDuration={!form.duration.trim() ? (seconds) => set({ duration: formatDuration(seconds) }) : undefined}
          />

          {followUpDone ? (
            <Typography variant="body2" color="text.secondary">
              The follow-up was done {dayjs(call.followUpDoneAt).format('D MMM YYYY, HH:mm')}. A new one goes on the person's latest call.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                size="small"
                type="datetime-local"
                label="Follow up"
                value={form.followUpAt}
                onChange={(event) => set({ followUpAt: event.target.value })}
                InputLabelProps={{ shrink: true }}
                error={Boolean(problemOf('followUpAt'))}
                helperText={problemOf('followUpAt') ?? 'Only on the person\'s latest call'}
                sx={{ flex: 1 }}
              />
              {form.followUpAt && (
                <Button size="small" sx={{ mt: 0.5 }} onClick={() => set({ followUpAt: '' })}>
                  Remove
                </Button>
              )}
            </Box>
          )}

          {refusal && !refusal.field && <Alert severity="error">{refusal.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          {changes ? `${changes} ${changes === 1 ? 'change' : 'changes'}` : 'No changes yet'}
        </Typography>
        <Button onClick={() => onClose(false)} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={loading || !changes || Object.keys(problems).length > 0}>
          {loading ? 'Saving…' : 'Save correction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
